import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import featuresService from '../../services/featuresService';

const initialState = {
  features: [],
  isLoading: false,
  error: null,
  success: false,
};

export const fetchFeatures = createAsyncThunk(
  'features/fetchAll',
  async (_, thunkAPI) => {
    try {
      return await featuresService.getAll();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch features';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchFeaturesByPetType = createAsyncThunk(
  'features/fetchByPetType',
  async (petTypeId, thunkAPI) => {
    try {
      return await featuresService.getByPetType(petTypeId);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch features';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createFeature = createAsyncThunk(
  'features/create',
  async (data, thunkAPI) => {
    try {
      return await featuresService.create(data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create feature';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateFeature = createAsyncThunk(
  'features/update',
  async ({ id, data }, thunkAPI) => {
    try {
      return await featuresService.update(id, data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update feature';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteFeature = createAsyncThunk(
  'features/delete',
  async (id, thunkAPI) => {
    try {
      await featuresService.delete(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete feature';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const toggleFeatureStatus = createAsyncThunk(
  'features/toggleStatus',
  async (id, thunkAPI) => {
    try {
      return await featuresService.toggleStatus(id);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to toggle feature status';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const featuresSlice = createSlice({
  name: 'features',
  initialState,
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatures.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFeatures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.features = action.payload;
      })
      .addCase(fetchFeatures.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFeaturesByPetType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFeaturesByPetType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.features = action.payload;
      })
      .addCase(fetchFeaturesByPetType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createFeature.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createFeature.fulfilled, (state, action) => {
        state.isLoading = false;
        state.features.push(action.payload);
        state.success = true;
      })
      .addCase(createFeature.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateFeature.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateFeature.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.features.findIndex((f) => f._id === action.payload._id);
        if (index !== -1) {
          state.features[index] = action.payload;
        }
        state.success = true;
      })
      .addCase(updateFeature.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteFeature.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteFeature.fulfilled, (state, action) => {
        state.isLoading = false;
        state.features = state.features.filter((f) => f._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteFeature.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleFeatureStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(toggleFeatureStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.features.findIndex((f) => f._id === action.payload._id);
        if (index !== -1) {
          state.features[index] = action.payload;
        }
      })
      .addCase(toggleFeatureStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess, clearError } = featuresSlice.actions;
export default featuresSlice.reducer;
